// Outside Services Transfer - Fabric -- printable voucher (jsPDF + autotable).
// Simple standard print (agreed 12-Oct-26): header, the fabric lines with the
// service rate / amount, a total and signature lines. The issue's average-rate
// value is not printed.
//
// Takes the same { master, lines } the form loads from /ospFabIssue/getById,
// so print and screen can never disagree. Same look as servicePurchasePrint.js.
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const qtyFmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 3 });

const INK = [31, 41, 55];
const MUTED = [107, 114, 128];
const RULE = [228, 231, 238];
const ACCENT = [63, 14, 128];

export function buildOspFabricIssuePdf({ master, lines }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 36;
  const companyName = localStorage.getItem("companyName") || "";
  const username = localStorage.getItem("username") || "";

  // ---- Title block ----
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(companyName, M, M + 6);
  doc.setFontSize(12);
  doc.setTextColor(...ACCENT);
  doc.text("Outside Services Transfer - Fabric", M, M + 26);
doc.setFont("helvetica", "bold");
doc.setFontSize(18);
doc.setTextColor(...INK);
doc.text(
  "Outward Gatepass (Returnable)",
  pageW / 2,
  M + 26,
  { align: "center" }
);

  // Doc no / date box on the right.
  const boxW = 210;
  const boxX = pageW - M - boxW;
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.8);
  doc.roundedRect(boxX, M - 10, boxW, 48, 4, 4);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text("Doc No", boxX + 10, M + 5);
  doc.text("Date", boxX + 10, M + 25);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  doc.text(String(master.doc_no || "-"), boxX + boxW - 10, M + 5, { align: "right" });
  doc.text(
    master.doc_date ? moment(master.doc_date).format("DD-MMM-YYYY hh:mm A") : "-",
    boxX + boxW - 10,
    M + 25,
    { align: "right" },
  );

  doc.setDrawColor(...RULE);
  doc.line(M, M + 66, pageW - M, M + 66);

  // ---- Header fields: two columns of label / value ----
  const fields = [
    ["Vendor", master.supplier_name || "-"],
    ["Work Type", master.serv_type_desc || "-"],
    ["Article", master.article_label || "-"],
    ["Job Card", master.jc_label || "-"],
    ["Location", master.sub_inv_desc || "-"],
    ["Issue Note", master.issue_no ? `# ${master.issue_no}` : "-"],
  ];
  // Embroidery: the Fixed stitches the lines were priced with.
  const savedFixed = (lines.find((l) => l.fixed != null) || {}).fixed;
  if (Number(master.serv_type_id) === 4 && savedFixed != null) {
    fields.push(["Fixed Stitches", qtyFmt(savedFixed)]);
  }
  let y = M + 86;
  const colW = (pageW - 2 * M) / 2;
  fields.forEach(([label, value], i) => {
    const x = M + (i % 2) * colW;
    const rowY = y + Math.floor(i / 2) * 18;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text(label, x, rowY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...INK);
    doc.text(String(value), x + 80, rowY, { maxWidth: colW - 90 });
  });
  y += Math.ceil(fields.length / 2) * 18;
  if (master.remarks) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text("Remarks", M, y);
    doc.setTextColor(...INK);
    doc.setFontSize(10);
    const wrapped = doc.splitTextToSize(String(master.remarks), pageW - 2 * M - 80);
    doc.text(wrapped, M + 80, y);
    y += wrapped.length * 12;
  }

  // ---- Lines ----
  // Embroidery (Work Type 4) prints its three inputs after Qty; the two fabric
  // columns narrow to make room. The Fixed each line was saved with sits in
  // the header (one value per document in practice).
  const isEmbroidery = Number(master.serv_type_id) === 4;
  let totalQty = 0;
  let totalAmt = 0;
  const body = lines.map((l, i) => {
    const qty = Number(l.qty) || 0;
    const amt = Number(l.amount) || 0;
    totalQty += qty;
    totalAmt += amt;
    return [
      i + 1,
      ...(isEmbroidery ? [l.compnt_desc || ""] : []),
      l.item_label || "",
      ...(isEmbroidery
        ? [
            l.stitches != null ? qtyFmt(l.stitches) : "",
            l.rate_per_yrd != null ? Number(l.rate_per_yrd).toString() : "",
            l.heads != null ? Number(l.heads).toString() : "",
            l.repeats != null ? Number(l.repeats).toString() : "",
            qtyFmt(qty),
            l.uom_desc || "",
          ]
        : [l.uom_desc || "", qtyFmt(qty)]),
      money(l.rate),
      money(amt),
      Number(l.waste_perc || 0).toFixed(2),
      l.ret_item_label || "",
      l.remarks || "",
    ];
  });
  // Embroidery also prints the line's Component (28-Oct-26) right after #, and
  // reads like the form: Stitches, Rate / Yrd, Heads, Repeats, then the Qty they
  // give and its UOM. Later columns move along (c, extra) and narrow to fit.
  const c = isEmbroidery ? 1 : 0;
  const extra = isEmbroidery ? 4 : 0;
  const fabricW = isEmbroidery ? 90 : 170;
  const qtyCol = isEmbroidery ? 7 : 3;
  const embrStyles = isEmbroidery
    ? {
        1: { cellWidth: 70 },
        3: { cellWidth: 42, halign: "right" },
        4: { cellWidth: 42, halign: "right" },
        5: { cellWidth: 42, halign: "right" },
        6: { cellWidth: 42, halign: "right" },
        7: { cellWidth: 50, halign: "right" },
        8: { cellWidth: 35 },
      }
    : {
        2: { cellWidth: 45 },
        3: { cellWidth: 60, halign: "right" },
      };

  doc.autoTable({
    startY: y + 8,
    margin: { left: M, right: M, bottom: 90 },
    head: [
      [
        "#",
        ...(isEmbroidery ? ["Component"] : []),
        "Fabric Sent",
        ...(isEmbroidery
          ? ["Stitches", "Rate / Yrd", "Heads", "Repeats", "Qty", "UOM"]
          : ["UOM", "Qty"]),
        "Rate",
        "Amount",
        "Waste %",
        "Receiving Fabric",
        "Remarks",
      ],
    ],
    body,
    foot: [
      [
        "",
        ...(isEmbroidery ? [""] : []),
        "Total",
        ...(isEmbroidery
          ? ["", "", "", "", qtyFmt(totalQty), ""]
          : ["", qtyFmt(totalQty)]),
        "",
        money(totalAmt),
        "",
        "",
        "",
      ],
    ],
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      textColor: INK,
      lineColor: RULE,
      lineWidth: 0.6,
      cellPadding: 5,
      overflow: "linebreak",
    },
    headStyles: { fillColor: [247, 248, 250], textColor: [75, 85, 99], fontStyle: "bold" },
    footStyles: { fillColor: [247, 248, 250], textColor: INK, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 26, halign: "center" },
      [1 + c]: { cellWidth: fabricW },
      ...embrStyles,
      [4 + c + extra]: { cellWidth: isEmbroidery ? 55 : 60, halign: "right" },
      [5 + c + extra]: { cellWidth: isEmbroidery ? 65 : 75, halign: "right" },
      [6 + c + extra]: { cellWidth: isEmbroidery ? 45 : 50, halign: "right" },
      [7 + c + extra]: { cellWidth: fabricW },
      [8 + c + extra]: { cellWidth: "auto" },
    },
    // Right-align the numeric foot cells too.
    didParseCell: (data) => {
      if (data.section === "foot" && [qtyCol, 5 + c + extra].includes(data.column.index)) {
        data.cell.styles.halign = "right";
      }
    },
  });

  // ---- Signatures, under the table (new page if it does not fit) ----
  let sigY = doc.lastAutoTable.finalY + 70;
  if (sigY > pageH - 60) {
    doc.addPage();
    sigY = M + 70;
  }
  const sigs = ["Prepared By", "Approved By", "Received By (Vendor)"];
  const sigW = (pageW - 2 * M) / sigs.length;
  doc.setDrawColor(...MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  sigs.forEach((s, i) => {
    const x = M + i * sigW + 20;
    doc.line(x, sigY, x + sigW - 60, sigY);
    doc.text(s, x + (sigW - 60) / 2, sigY + 13, { align: "center" });
  });

  // ---- Page footer on every page ----
  const pages = doc.internal.getNumberOfPages();
  const printedOn = moment().format("DD-MMM-YYYY hh:mm A");
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(`Printed on ${printedOn}${username ? ` by ${username}` : ""}`, M, pageH - 18);
    doc.text(`Page ${p} of ${pages}`, pageW - M, pageH - 18, { align: "right" });
  }
  return doc;
}

// Opens the PDF in a new tab. Pass a window opened synchronously in the click
// handler when the data is fetched first, or the popup blocker eats it.
export function openOspFabricIssuePdf(data, targetWindow) {
  const doc = buildOspFabricIssuePdf(data);
  const url = doc.output("bloburl");
  if (targetWindow && !targetWindow.closed) {
    targetWindow.location.href = url;
  } else {
    window.open(url, "_blank");
  }
}
